import React, { useContext, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';

import UserAvatar from 'app/components/CommonStyled/UserAvatar';
import {
  PeopleSearchBarTabletGroupsContainer,
  PeopleSearchBarTabletGroupsLabel,
} from 'app/components/People/PeopleContent/PeopleSearch/PeopleSearchBar/styled';
import useResourcesReset from 'app/components/Resources/hooks/useResourcesReset';
import Tooltip from 'app/components/Tooltip';
import { ResetIcon, SearchIconSmall, Sparkle } from 'app/components/shared/icons';
import { AnimatePresence } from 'framer-motion';
import useIsMobile from 'hooks/useIsMobile';
import { allGroupsSelector, promptSelector, setPrompt } from 'store/reducers/nlp/nlp-reducer';
import { arrayToOxford, singularOrPlural } from 'utils';

import { ResourcesNLPSearchContext } from '../ResourcesNLPSearchContext';
import {
  ResourceSearchBarWrapper,
  ResourceSearchBarContainer,
  ResourceSearchBarResetContainer,
  ResourceSearchBarInformationContainer,
  ResourceSearchBarInput,
  ResourceSearchBarGroupsWrapper,
  ResourceSearchBarGroupContainer,
  ResourceSearchBarGroupLabel,
} from './styled';

export default function ResourcesNLPSearchBar() {
  const context = useContext(ResourcesNLPSearchContext);
  const isMobile = useIsMobile();

  const resourceSearchReset = useResourcesReset();

  const prompt = useSelector(promptSelector);
  const allGroups = useSelector(allGroupsSelector);

  const inputRef = useRef<HTMLTextAreaElement>(null);

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !context.nlpSearchQuery?.isLoading) {
      context.sendSearch();
      e.preventDefault();
      e.currentTarget.blur();
    }
  };

  const handleResetClick = () => {
    context.setSearchValue('');
    context.setSearchFocused(false);
    context.setShowHistory(false);
    resourceSearchReset();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    context.setSearchValue(e.target.value);
  };

  const handleInputBlur = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    context.setSearchFocused(false);
    if (e.currentTarget.value === '') {
      setPrompt(null);
    }
  };

  const handleInputFocus = () => {
    context.setSearchFocused(true);
  };

  useEffect(() => {
    if (context.searchValue !== '' && inputRef.current) {
      inputRef.current.style.height = !isMobile ? '30px' : '26px';
      inputRef.current.style.height = `${inputRef.current.scrollHeight}px`;
    }

    if (context.searchValue === '' && inputRef.current) {
      inputRef.current.style.height = !isMobile ? '30px' : '26px';
    }
  }, [context.searchValue]);

  return (
    <ResourceSearchBarWrapper>
      <ResourceSearchBarContainer>
        <UserAvatar size={40} highlighted />
        <ResourceSearchBarInformationContainer>
          <ResourceSearchBarInput
            data-id="nlp-search-input"
            ref={inputRef}
            value={context.searchValue || ''}
            placeholder="I’m looking for..."
            onChange={handleInputChange}
            onKeyDown={handleKeyPress}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            onInput={() => {
              inputRef.current.style.height = `${inputRef.current.scrollHeight}px`;
            }}
          />
          <AnimatePresence>
            {allGroups.length > 0 ? (
              !isMobile ? (
                <ResourceSearchBarGroupsWrapper>
                  {allGroups.map((group) => (
                    <ResourceSearchBarGroupContainer key={group}>
                      <SearchIconSmall fill={'rgba(23, 28, 51, 0.5)'} width={15} height={15} />
                      <ResourceSearchBarGroupLabel>{group}</ResourceSearchBarGroupLabel>
                    </ResourceSearchBarGroupContainer>
                  ))}
                </ResourceSearchBarGroupsWrapper>
              ) : (
                <>
                  <Tooltip text={arrayToOxford(allGroups)}>
                    <PeopleSearchBarTabletGroupsContainer>
                      <Sparkle width={20} height={20} fill={'rgba(23, 28, 51, 0.5)'} />
                      <PeopleSearchBarTabletGroupsLabel>
                        {allGroups.length} {singularOrPlural(allGroups.length, 'SOURCE', 'SOURCES')}{' '}
                        REFERENCED
                      </PeopleSearchBarTabletGroupsLabel>
                    </PeopleSearchBarTabletGroupsContainer>
                  </Tooltip>
                </>
              )
            ) : null}
          </AnimatePresence>
        </ResourceSearchBarInformationContainer>
      </ResourceSearchBarContainer>
      {prompt && (
        <ResourceSearchBarResetContainer onClick={handleResetClick}>
          <ResetIcon width={30} height={30} fill={'var(--color-primary)'} fillOpacity="1" />
        </ResourceSearchBarResetContainer>
      )}
    </ResourceSearchBarWrapper>
  );
}
